import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import { Button, Modal, ProgressBar } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { API_BASE } from "../api";

function Chatbot() {
  useEffect(() => {
    document.title = "Chatbot";
  }, []);

  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]); // Persist video chunks across events
  const [recording, setRecording] = useState(false);
  const [timerSec, setTimerSec] = useState(0);
  const [showWebcam, setShowWebcam] = useState(false);
  const [deelnemerId, setDeelnemerId] = useState("");
  // validate deelnemer id: only digits, max 3 chars
  const isValidDeelnemerId = /^\d{3}$/.test(deelnemerId);
  const handleDeelnemerChange = (e) => {
    // keep only digits and max length 3
    const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, 3);
    setDeelnemerId(digits);
  };

  // Participant lookup state: null = not checked / input invalid, true = found, false = not found
  const [participantName, setParticipantName] = useState(null);
  const [participantFound, setParticipantFound] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const lookup = async (id) => {
      try {
        const res = await fetch(`${API_BASE}/deelnemers`);
        if (!res.ok) throw new Error("Network response was not ok");
        const data = await res.json();
        if (cancelled) return;
        const found = data.find((p) => String(p.id) === id || String(p.id).padStart(3, "0") === id);
        if (found) {
          setParticipantName(found.naam || found.name || `#${found.id}`);
          setParticipantFound(true);
        } else {
          setParticipantName(null);
          setParticipantFound(false);
        }
      } catch (err) {
        if (!cancelled) {
          setParticipantName(null);
          setParticipantFound(false);
        }
      }
    };

    if (isValidDeelnemerId) {
      setParticipantFound(null); // pending
      lookup(deelnemerId);
    } else {
      setParticipantName(null);
      setParticipantFound(null);
    }

    return () => {
      cancelled = true;
    };
  }, [deelnemerId, isValidDeelnemerId]);
  const [phase, setPhase] = useState("idle"); // idle, intro, questions, matching
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [startTimeISO, setStartTimeISO] = useState(null);
  const [intervalId, setIntervalId] = useState(null);
  const [timeoutId, setTimeoutId] = useState(null);
  const [matchingProgress, setMatchingProgress] = useState(0);
  const [showBedanktModal, setShowBedanktModal] = useState(false);
  const [forcedTimeout, setForcedTimeout] = useState(false);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: "user",
  };

  const questions = [
    {
      q: "Which chair is the most polite during meetings?",
      choices: ["The stool that doesn't squeak", "The bean bag that always listens", "The office chair that suppresses sighs"],
    },
    {
      q: "Which greeting would you use for a cactus?",
      choices: ["Fist bump", "Elbow nudge", "Courtly nod"],
    },
    {
      q: "How do you respond to a compliment from a pigeon?",
      choices: ["Return the bread", "Fold the newspaper", "Applaud with wings"],
    },
    {
      q: "What is your favorite break sound?",
      choices: ["A silent gong", "A whispering trumpet", "An apologetic triangle tap"],
    },
    {
      q: "How do you share an elevator with a shadow?",
      choices: ["The shadow goes first", "Through the gap together", "Take the stairs, out of courtesy"],
    },
    {
      q: "If clouds asked for directions, where would you send them?",
      choices: ["Above the bakery", "Toward the polite mountain", "The corner of yesterday and noon"],
    },
    {
      q: "Which utensil would win a duel in a teacup?",
      choices: ["The spoon with a secret", "The fork that dances", "The timid chopstick"],
    },
    {
      q: "How would you apologize to a stapler?",
      choices: ["With an elegant paper bow", "By returning its missing pin", "Sing a short lullaby"],
    },
    {
      q: "Which scent represents productive daydreaming?",
      choices: ["Warm toast with ideas", "Minty brainstorm breeze", "Laundry folded by philosophers"],
    },
    {
      q: "What do you wear to a meeting with a librarian ghost?",
      choices: ["Quiet shoes", "A jacket of footnotes", "A scarf of marginalia"],
    },
    {
      q: "If your shadow could bring snacks, what would it bring?",
      choices: ["Invisible cookies", "Half-moon sandwiches", "A bag of thoughtful crumbs"],
    },
    {
      q: "Which alarm clock would politely wake the moon?",
      choices: ["A soft tambourine", "A sonnet on a string", "A tea kettle in top hat"],
    },
    {
      q: "Where does a wandering bookmark like to sleep?",
      choices: ["Between chapter hugs", "Under the margins", "On a gentle footnote"],
    },
    {
      q: "How do you invite a library of spoons to a party?",
      choices: ["Send a handwritten menu", "Leave a shiny trail", "Whisper 'dessert' thrice"],
    },
    {
      q: "Which hat best represents a thoughtful spreadsheet?",
      choices: ["The fedora of formulas", "A bowler filled with tabs", "A beanie that sorts rows"],
    },
  ];

  // safe accessor for current question to avoid reading undefined
  const currentQuestion = questions[questionIndex] || { q: "", choices: [] };

  // When START is pressed, show the intro text before questions
  const handleStart = () => {
    if (!deelnemerId || !webcamRef.current || !webcamRef.current.stream) return;
    setPhase("intro");
  };

  // Called when user presses "Proceed to questionnaire" in the intro modal.
  // This starts recording, timers and shows the first question.
  const proceedToQuestions = () => {
    setPhase("questions");
    setRecording(true);
    setTimerSec(0);
    setStartTimeISO(new Date().toISOString());
    setForcedTimeout(false);

    // start media recorder
    const stream = webcamRef.current.stream;
    let options = { mimeType: "video/webm;codecs=vp9,opus" };
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      options = { mimeType: "video/webm;codecs=vp8,opus" };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: "video/webm" };
      }
    }
    // Clear previous chunks
    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(stream, options);
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };
    mediaRecorder.onstop = () => {
      // Defensive: wait a tick to ensure all dataavailable events have fired
      setTimeout(() => {
        const allChunks = chunksRef.current;
        if (allChunks.length === 0) {
          console.warn("No video data recorded: uploading 0-byte file.");
        }
        const blob = new Blob(allChunks, { type: mediaRecorder.mimeType });
        uploadVideo(blob);
        // stop tracks
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch (e) {}
        setRecording(false);
      }, 0);
    };
    mediaRecorder.start(100); // request data every 100ms for reliability

    // timer tick every second (UI)
    const id = setInterval(() => {
      setTimerSec((t) => t + 1);
    }, 1000);
    setIntervalId(id);

    // strict 60s timeout to trigger matching regardless of progress
    const toId = setTimeout(() => {
      setForcedTimeout(true);
      setPhase("matching");
      // start matching in forced mode
      startMatchingPhase(true);
    }, 60000);
    setTimeoutId(toId);
  };

  const triggerFinish = () => {
    // keep showing bedankt modal and stopping recording
    setShowBedanktModal(true);
    // stop recorder if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch (e) {}
    }
    // After 5 seconds, hide modal and reset (and clear deelnemer id per request)
    setTimeout(() => {
      setShowBedanktModal(false);
      resetAll();
    }, 5000);
  };

  const uploadVideo = async (blob) => {
    try {
      const formData = new FormData();
      const filename = `${uuidv4()}.webm`;
      formData.append("video", blob, filename);
      formData.append("deelnemer_id", deelnemerId);
      formData.append("start_time", startTimeISO);
      formData.append("end_time", new Date().toISOString());

      const response = await fetch(`${API_BASE}/videos`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        console.error("Error uploading video");
      }
    } catch (err) {
      console.error("Error uploading video", err);
    }
  };

  const handleAnswer = (choice) => {
    setAnswers((a) => [...a, choice]);
    // blur active element so button doesn't stay highlighted
    setTimeout(() => {
      try {
        if (document.activeElement && typeof document.activeElement.blur === "function") {
          document.activeElement.blur();
        }
      } catch (e) {}
    }, 0);
    const next = questionIndex + 1;

    // if user finished early, cancel the scheduled forced timeout
    if (next >= questions.length) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        setTimeoutId(null);
      }
      // finished questions
      // move to matching phase; set questionIndex to a safe value
      setQuestionIndex(next);
      setPhase("matching");
      // start matching (will end the flow and show bedankt)
      startMatchingPhase(false);
    } else {
      setQuestionIndex(next);
    }
  };

  const startMatchingPhase = (isForced = false) => {
    setMatchingProgress(0);
    // animate 0 -> 100 in 3s
    const duration = 3000;
    const stepMs = 50;
    const steps = Math.ceil(duration / stepMs);
    let current = 0;
    const inc = 100 / steps;
    const mid = setInterval(() => {
      current += inc;
      setMatchingProgress(Math.min(100, Math.round(current)));
      if (current >= 100) {
        clearInterval(mid);
        // stop recorder if active to flush upload
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          try {
            mediaRecorderRef.current.stop();
          } catch (e) {}
        }
        // show bedankt modal, wait and reset
        setShowBedanktModal(true);
        setTimeout(() => {
          setShowBedanktModal(false);
          resetAll();
        }, 5000);
      }
    }, stepMs);
    // store as intervalId so we can clear on unmount if needed
    setIntervalId((prev) => {
      // clear previous if any
      if (prev) clearInterval(prev);
      return mid;
    });
  };

  const resetAll = () => {
    setPhase("idle");
    setQuestionIndex(0);
    setAnswers([]);
    setStartTimeISO(null);
    setMatchingProgress(0);
    setTimerSec(0);
    setForcedTimeout(false);
    // clear deelnemer id per request
    setDeelnemerId("");
    // clear timers
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    // stop media recorder forcefully
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } catch (e) {}
    // stop tracks if any
    try {
      if (webcamRef.current && webcamRef.current.stream) {
        webcamRef.current.stream.getTracks().forEach((t) => t.stop());
      }
    } catch (e) {}
    setRecording(false);
    setShowWebcam(false);
    // Clear video chunks
    chunksRef.current = [];
  };

  // Toggle webcam preview with "W" key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "w" || e.key === "W") {
        setShowWebcam((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {}
      try {
        if (webcamRef.current && webcamRef.current.stream) {
          webcamRef.current.stream.getTracks().forEach((track) => track.stop());
        }
      } catch (e) {}
      // Clear video chunks
      chunksRef.current = [];
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", gap: "1rem" }}>
      <h1 style={{ fontSize: "3em", color: "white", textAlign: "center", margin: 0 }}>Chatbot</h1>
      {phase === "idle" && (
        <>
          <input
            id="deelnemerId"
            type="text"
            value={deelnemerId}
            onChange={handleDeelnemerChange}
            placeholder="000"
            maxLength={3}
            inputMode="numeric"
            pattern="\d{3}"
            style={{ width: "30%", fontSize: "5em", textAlign: "center", padding: "0.2em", borderRadius: "0.6em" }}
          />
          {/* Participant name or not-found message */}
          <div style={{ fontSize: "1.5em", marginTop: "0.2em" }}>
            {participantFound === null ? "" : participantFound === true ? participantName : "Participant not found"}
          </div>
          <Button
            variant="primary"
            className="start-button"
            onClick={handleStart}
            disabled={!participantFound || !webcamRef.current || !webcamRef.current.stream}
            style={{ width: "30%", fontSize: "5em", marginTop: "0.5em", borderRadius: "0.6em" }}
          >
            START
          </Button>
        </>
      )}

      {/* Intro modal that appears before the first question */}
      <Modal show={phase === "intro"} centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <h4 style={{ margin: 0 }}>Welcome</h4>
        </Modal.Header>
        <Modal.Body>
          <p>
            We are eternally grateful to you, fellow human, for agreeing to participate in a test conversation with our newly developed virtual human, of which we are immensely proud! Since this virtual human is able to adapt fully to your needs as a conversational partner, we need to gather some information about you through a short questionnaire. Once you are done with the questionnaire, you will be automatically connected to the virtual human's video chat.
          </p>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
            <Button onClick={proceedToQuestions}>Proceed to questionnaire</Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Question modal flow */}
      <Modal show={phase === "questions" && questionIndex < questions.length} centered backdrop="static" keyboard={false}>
        <Modal.Header>
          <div style={{ width: "100%" }}>
            <ProgressBar now={((questionIndex + 1) / questions.length) * 100} label={`${questionIndex + 1}/${questions.length}`} />
          </div>
        </Modal.Header>
        <Modal.Body>
          <h4>{currentQuestion.q}</h4>
          <div style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
            {currentQuestion.choices.map((c, i) => (
              <Button key={i} onClick={() => handleAnswer(c)}>{c}</Button>
            ))}
          </div>
        </Modal.Body>
      </Modal>

      {/* Matching phase */}
      {phase === "matching" && (
        <div style={{ textAlign: "center", width: "60%" }}>
          <div style={{ marginBottom: "1rem", fontSize: "1.6em" }}>Your data is being processed.</div>
          <ProgressBar now={matchingProgress} animated striped label={`${matchingProgress}%`} />
        </div>
      )}

      {/* Bedankt modal (triggered after matching) */}
      <Modal show={showBedanktModal} centered>
        <Modal.Body style={{ textAlign: "center", fontSize: "1.4em" }}>
          This part of the test is finished. When the whistle blows, you may proceed to the next section.
        </Modal.Body>
      </Modal>

      {/* Hidden but active webcam for functionality */}
      <div style={{ position: "absolute", display: "block", zIndex: -100, top: 0, width: 1280, height: 960, overflow: "hidden" }}>
        <Webcam
          audio={true}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={videoConstraints}
          width={1280}
          height={720}
          style={ showWebcam ? { visibility: "visible" } : { visibility: "hidden" } }
        />
      </div>
    </div>
  );
}

export default Chatbot;
